import { useState } from 'react';
import { messageActionSchema, type MessageAction } from '@avslack/shared';
import { triggerInteraction } from '../lib/api';
import { Icon } from './Icon';

/**
 * Interaktiv gombok egy integracio uzenete alatt.
 *
 * Ket fajta gomb van, es a kulonbseg latszik is:
 *  - **link**: sima megnyitas, semmi nem tortenik a rendszerben,
 *  - **akcio**: esemenyt kuld a kulso rendszernek, ami erre valaszolhat.
 *
 * Az akcio-gomb egy kattintas utan letiltodik. Nem esztetikai dontes: egy
 * "Elvallalom" gombot nem szerencses ketszer elkuldeni, mert a kulso rendszer
 * nem feltetlenul idempotens.
 */
export function MessageActionButtons({
  messageId,
  meta,
}: {
  messageId: string;
  meta: Record<string, unknown> | null;
}) {
  const [sent, setSent] = useState<Set<string>>(new Set());
  const [failed, setFailed] = useState<string | null>(null);

  const parsed = messageActionSchema.array().safeParse(meta?.actions);
  if (!parsed.success || parsed.data.length === 0) return null;

  const press = async (action: MessageAction) => {
    setSent((current) => new Set(current).add(action.id));
    setFailed(null);
    try {
      await triggerInteraction(messageId, action.id);
    } catch {
      // Sikertelen kuldesnel visszaengedjuk a gombot - kulonben a felhasznalo
      // ott all egy letiltott gombbal, es nem tud mit tenni.
      setSent((current) => {
        const next = new Set(current);
        next.delete(action.id);
        return next;
      });
      setFailed('Nem sikerült elküldeni. Próbáld újra.');
    }
  };

  return (
    <div className="mt-2">
      <div className="flex flex-wrap gap-1.5">
        {parsed.data.map((action) =>
          action.url ? (
            <a
              key={action.id}
              href={action.url}
              target="_blank"
              rel="noopener noreferrer"
              className={buttonClass(action.style)}
            >
              {action.label}
              <span aria-hidden className="ml-1 text-2xs opacity-60">
                <Icon name="tovabbitas" />
              </span>
            </a>
          ) : (
            <button
              key={action.id}
              type="button"
              disabled={sent.has(action.id)}
              onClick={() => void press(action)}
              className={`${buttonClass(action.style)} disabled:cursor-default disabled:opacity-50`}
            >
              {sent.has(action.id) ? `${action.label} ✓` : action.label}
            </button>
          ),
        )}
      </div>

      {failed && (
        <p role="alert" className="mt-1 text-2xs text-danger">
          {failed}
        </p>
      )}
    </div>
  );
}

function buttonClass(style: MessageAction['style']): string {
  const base =
    'rounded-control px-2.5 py-1 text-xs font-medium transition-colors duration-(--duration-quick)';

  if (style === 'primary') return `${base} bg-accent text-accent-ink hover:bg-accent-hover`;
  if (style === 'danger') return `${base} bg-[var(--danger)] text-white hover:brightness-110`;
  return `${base} border border-border bg-surface-raised text-ink-muted hover:bg-surface-hover hover:text-ink`;
}
